<template>
  <v-navigation-drawer
    v-model="showDrawer"
    clipped
    app
    :width="300"
  >
    <v-layout>
      <v-flex>
        <PoLineItemsActions
          :poClients="poClients"
          :isMyClient="isMyClient"
          :isMyEmployeeClient="isMyEmployeeClient"
          :disableActionButton="disableActionButton"
          :showIsLoadingOnButton="showIsLoadingOnButton"
          :itemsFilteredByRestrictions="itemsFilteredByRestrictions"
          :groupedItemsLength="groupedItems.length"
          @menu-action-click="menuActionClick"
          v-if="displayActions"
        />
        <PoLineItemsFilter
          :filter="filter"
          :disabled="loading"
          @filter-changed="onFilterChanged"
        />
        <v-list dense>
          <v-list-item
            class="elevation-1 pl-2"
          >
            <v-list-item-avatar
              class ="pl-2"
              v-if="showCheckboxes"
            >
              <v-checkbox
                v-if="showCheckboxes"
                v-model="allSelected"
                :disabled="disableCheckbox"
                @change="toggleItemsSelected"
                :title="$t('poLineItemsList.checkboxTitle', 1)"
              >
              </v-checkbox>
            </v-list-item-avatar>

            <v-list-item-content
              class="item-list-header-action user-select-none"
              @click="toggleSorting('itemNumber')"
              :title="$t('poLineItemsList.sortIconTitle')"
            >
              <v-list-item-title>
                <span class="grey--text">
                  {{ $t('poLineItemsList.itemNumberHeaderText') }}
                </span>
                <v-slide-x-transition>
                  <v-icon
                    :class="resolveSortOrderClass('itemNumber')"
                    :disabled="resolveSortDisabled('itemNumber')"
                    small
                    color="grey darken-4"
                  >
                    {{ 'arrow_downward' }}
                  </v-icon>
                </v-slide-x-transition>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-action
              class="item-list-header-action status-action user-select-none"
              @click="toggleSorting('status')"
              :title="$t('poLineItemsList.sortIconTitle')"
            >
              <v-list-item-title
                class="text-xs-center text-center"
              >
                <span class="grey--text">
                  {{ $t('poLineItemsList.statusHeaderText') }}
                </span>
                <v-icon
                  :class="resolveSortOrderClass('status')"
                  small
                  :disabled="resolveSortDisabled('status')"
                  color="grey darken-4"
                >
                  {{ 'arrow_downward' }}
                </v-icon>
              </v-list-item-title>
            </v-list-item-action>
          </v-list-item>
          <v-divider  />
          <v-progress-linear
            class="ma-0"
            height="2"
            :color="progressBarColor"
            value="0"
            :indeterminate="showProgressBar"
          >
          </v-progress-linear>
          <template
            v-for="(item, index) in itemsFiltered"
          >
            <v-list-item
              :key="item.itemNumber"
              :class="{
                'grey lighten-2': item.selected
              }"
              class="pl-2"
              :inactive="disableLineItem(item)"
              @click="disableLineItem(item)? null : onCurrentItemChanged(item.itemNumber)"
            >
              <v-list-item-avatar
                class ="pl-2"
                @click.stop v-if="showCheckboxes"
              >
                <v-checkbox
                  v-model="selectedItemsNumbers"
                  :value="item.itemNumber"
                  :disabled="disableCheckbox"
                  @change="toggleItemSelected"
                  :title="$t('poLineItemsList.checkboxTitle', 0)"
                >
                </v-checkbox>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>{{ item.itemNumber }}</v-list-item-title>
              </v-list-item-content>

              <v-list-item-action
                class="status-action"
              >
                <PoLineItemStatus
                  class="elevation-1"
                  :po-status="item.status"
                  small
                >
                </PoLineItemStatus>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              v-if="index + 1 < groupedItems.length"
              :key="index"
            ></v-divider>
          </template>
          <v-list-item
            v-if="groupedItems.length === 0"
          >
            <v-list-item-content>
              <v-list-item-title
                class="text-xs-center grey--text"
              >
                {{ $t('poLineItemsList.noLineItemsText') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-flex>
    </v-layout>
  </v-navigation-drawer>
</template>

<script>
import PoLineItemStatus from '@/components/PoDetails/PoLineItemStatus.vue';
import { sortBy } from 'lodash';

import PoLineItemsActions from '@/components/PoDetails/SideBar/PoLineItemsActions.vue';
import PoLineItemsFilter from '@/components/PoDetails/SideBar/PoLineItemsFilter.vue';
import { getGroupNumber } from '@/helpers/skuHelper';

export default {
  name: 'PoLineItemsList',
  components: {
    PoLineItemStatus,
    PoLineItemsActions,
    PoLineItemsFilter,
  },
  data: () => ({
    selectedItemsNumbers: [],
    allSelected: false,
    filter: null,
    sortByInfos: [
      {
        name: 'itemNumber',
        enableSorting: true,
        sortingOrder: true,
      },
      {
        name: 'status',
        enableSorting: false,
        sortingOrder: true,
      },
    ],
    currentClickedMenuItem: {},
  }),
  computed: {
    showCheckboxes() {
      return this.$isAllowed('view', 'SelectManyItems', {
        clients: this.poClients,
        isMyClient: this.isMyClient,
        isMyEmployeeClient: this.isMyEmployeeClient,
      }) && this.displayCheckboxes;
    },
    disableCheckbox() {
      return this.loading;
    },
    showProgressBar() {
      return this.loading || this.currentLineInfoLoading;
    },
    progressBarColor() {
      if (!this.showProgressBar) {
        return 'white';
      }

      return 'primary';
    },
    showDrawer: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit('visibility-changed', val);
      },
    },
    itemsFiltered() {
      let result = this.getFiltered(this.groupedItems);

      for (let i = 0; i < this.sortByInfos.length; i += 1) {
        const sortByInfo = this.sortByInfos[i];

        if (sortByInfo.enableSorting) {
          result = sortBy(result, sortByInfo.name, sortByInfo.sortingOrder);
        }
      }

      return result;
    },
  },
  watch: {
    groupedItems(val) {
      if (val.length) {
        const { 0: firstItem } = val;
        const routeSku = this.$route.params.sku;
        const itemNumber = this.getSkuNumberFromRoute(routeSku);
        const isNotValidRouteSku = routeSku && !itemNumber;
        if (isNotValidRouteSku) {
          this.$router.push({
            name: 'notFound',
            params: { 0: '/notFound' },
          });
          return;
        }
        this.onItemSelected(!routeSku ? firstItem.itemNumber : itemNumber);
      }
    },
    itemsFiltered() {
      this.selectedItemsNumbers = [];
      this.allSelected = false;
    },
  },
  methods: {
    showIsLoadingOnButton(item) {
      return (this.submitting && this.currentClickedMenuItem === item)
            || (this.downloadingApproval && this.currentClickedMenuItem === item && item.actionName === 'download');
    },
    disableActionButton(actionName) {
      return this.lineSelectionDisabled
          || this.itemsFilteredByRestrictions(actionName).length === 0;
    },
    itemsFilteredByRestrictions(actionName) {
      return this.itemsFiltered.filter(x => this.selectedItemsNumbers.includes(x.itemNumber)
          && this.$isAllowed(actionName, 'LineItem', {
            status: x.status,
            client: x.client,
            isMyClient: x.isMyClient,
            isMyEmployeeClient: x.isMyEmployeeClient,
          }));
    },
    toggleSorting(sortByPropertyName) {
      const sortByInfo = this.sortByInfos.find(sbi => sbi.name === sortByPropertyName);

      if (sortByInfo.enableSorting && sortByInfo.sortingOrder) {
        sortByInfo.sortingOrder = false;
      } else if (sortByInfo.enableSorting && !sortByInfo.sortingOrder) {
        sortByInfo.enableSorting = false;
        sortByInfo.sortingOrder = true;
      } else {
        sortByInfo.enableSorting = true;
      }
    },
    disableLineItem(item) {
      return this.lineSelectionDisabled || item.selected;
    },
    menuActionClick(item) {
      this.currentClickedMenuItem = item;
      const {
        headerText,
        bodyText,
        confirmBtnText,
        enableNotes,
        isNotesRequired,
        confirmationColor,
      } = item.confirmationSettings;

      const restricted = this.itemsFilteredByRestrictions(item.actionName).length;
      const total = this.groupedItems.length;
      this.$confirm(`The <b>${restricted} of ${total}</b> lines ${bodyText}`,
        headerText,
        {
          width: 320,
          color: confirmationColor,
          yesLabel: confirmBtnText,
          noLabel: 'Cancel',
          enableNotes,
          isNotesRequired,
        })
        .then((payload) => {
          const { confirmed, notes } = payload;

          const lineItemsIds = this.groupedItems
            .filter(gi => this.selectedItemsNumbers.includes(gi.itemNumber))
            .map(gi => gi.lineItems)
            .reduce((a, b) => a.concat(b))
            .map(li => li.id);

          if (confirmed) {
            this.$emit(item.eventName, lineItemsIds, item.data, notes);
            this.resetSelectedNumbers();
          }
        });
    },
    toggleItemSelected() {
      this.allSelected = this.itemsFiltered.length === this.selectedItemsNumbers.length;
    },
    toggleItemsSelected(selected) {
      if (selected) {
        this.selectedItemsNumbers = this.itemsFiltered.map(i => i.itemNumber);
      } else {
        this.selectedItemsNumbers = [];
      }
    },
    onItemSelected(itemNumber) {
      const routeSku = this.$route.params.sku;
      if (!routeSku) {
        this.$router.replace({
          name: this.routeName,
          params: {
            purchaseOrderNumber: this.poNumber,
            sku: itemNumber,
          },
        });
        this.onItemSelected(itemNumber);
      }
      if (routeSku && !this.groupedItems.find(f => f.itemNumber === routeSku)) {
        this.$router.replace({
          name: this.routeName,
          params: {
            purchaseOrderNumber: this.poNumber,
            sku: itemNumber,
          },
        });
      }
      this.$emit('po-line-item-selected', itemNumber);
    },
    onCurrentItemChanged(itemNumber) {
      this.$router.push({
        name: this.routeName,
        params: {
          purchaseOrderNumber: this.poNumber,
          sku: itemNumber,
        },
      });
      this.onItemSelected(itemNumber);
    },
    resetSelectedNumbers() {
      this.selectedItemsNumbers = [];
      this.allSelected = false;
    },
    getFiltered(items) {
      if (!this.filter) {
        return items;
      }

      const trimedFilter = this.filter.trim();

      if (trimedFilter === '') {
        return items;
      }

      return items.filter(i => i.itemNumber.toLowerCase().includes(trimedFilter.toLowerCase()));
    },
    resolveSortDisabled(name) {
      const sortByInfo = this.sortByInfos.find(sbi => sbi.name === name);

      return !sortByInfo.enableSorting;
    },
    resolveSortOrderClass(name) {
      const sortByInfo = this.sortByInfos.find(sbi => sbi.name === name);

      if (sortByInfo.sortingOrder) {
        return 'asc';
      }

      return 'desc';
    },
    onFilterChanged(val) {
      this.filter = val;
    },
    getSkuNumberFromRoute(routeSku) {
      if (routeSku) {
        return getGroupNumber(this.groupedItems, routeSku);
      }
      return null;
    },
  },
  props: {
    items: Array,
    groupedItems: Array,
    show: Boolean,
    loading: Boolean,
    submitting: Boolean,
    downloadingApproval: Boolean,
    poClients: Array,
    isMyClient: Boolean,
    isMyEmployeeClient: Boolean,
    lineSelectionDisabled: Boolean,
    currentLineInfoLoading: Boolean,
    poNumber: String,
    sku: String,
    displayCheckboxes: Boolean,
    displayActions: Boolean,
    routeName: String,
  },
};
</script>

<style lang="scss" scoped>
  .status-action {
    min-width: 100px;
    -webkit-box-pack: center !important;
    -ms-flex-pack: center !important;
    justify-content: center !important;
  }

  .item-list-header-action {
    cursor: pointer;

    .v-icon.desc {
      -webkit-transform: rotate(-180deg);
      transform: rotate(-180deg);
    }
  }
</style>

<i18n>
{
  "en": {
    "poLineItemsList": {
      "checkboxTitle": "Tick/Untick the line item | Tick/Untick all line items",
      "sortIconTitle": "Click to sort",
      "itemNumberHeaderText": "Item #",
      "statusHeaderText": "Status",
      "noLineItemsText": "No Line Items found"
    }
  }
}
</i18n>
